import React from "react";
import FullScreenModal from "../FullScreenModal/FullScreenModal";
import { IReducer } from "../../state/reducer";
import { FullModalContainer } from "../FullModalContainer/FullModalContainer";
import { BrickInfoTotal } from "../../state/BrickInfoTotal/BrickInfoTotal";
import { Check, X } from "react-feather";
import "./BrickPackDetails.css";
import { ActionChange } from "../../state/BrickActions/ActionInfo/ActionInfoTypes";

interface BrickPackDetailsProps extends IReducer {
  packId: string; //action
  packTitle: string;
  isOpen: boolean;
  onClose: (arg0: boolean) => void;
}

const BrickPackDetails: React.FC<BrickPackDetailsProps> = ({
  packId,
  packTitle,
  isOpen,
  onClose,
  state,
}) => {
  const renderBrickPackProgress = () => {
    let total = 0;

    for (let i = 0; i < (state.actions.get(packId)?.outcome.length ?? 0); i++) {
      const brickId =
        state.actions.get(packId)?.outcome[i].brickId ?? "not_found";
      const brick = state.bricks.get(brickId);

      if (brick && brick.amount >= 0) {
        total++;
      }
    }

    return total;
  };

  const renderCosts = (_: number, cost: ActionChange) => {
    // Brick packs use flat costs (no scaling)
    const trueCost = cost.baseChangeValue;

    // Default to "brick" if type not specified (backward compatibility)
    const costType = cost.type || "brick";

    let hasEnough = false;
    let itemName = "";
    let currentAmount = 0;
    let currentCapacity = 0;

    if (costType === "brick") {
      const brick = state.bricks.get(cost.brickId);
      itemName = brick?.name ?? "Unknown Brick";
      currentAmount = brick?.amount ?? 0;
      currentAmount = isNaN(currentAmount) ? 0 : currentAmount;
      currentCapacity = brick?.capacity ?? 0;
      hasEnough = brick
        ? !Number.isNaN(brick.amount) && brick.amount >= trueCost
        : false;
    } else if (costType === "set") {
      const set = state.sets.get(cost.brickId);
      itemName = set?.name ?? "Unknown Set";
      currentAmount = set?.count ?? 0;
      currentCapacity = 0; // Sets don't have capacity
      hasEnough = set ? set.count >= trueCost : false;
    }

    if (hasEnough) {
      return (
        <tr className="tableRow" key={cost.brickId}>
          <td className="tableCell">{itemName}</td>
          <td className="tableCell">{trueCost}</td>
          <td className="tableCell">
            <Check size={32} className="costIcon metCostIcon" />
          </td>
          <td className="tableCell">
            {costType === "brick"
              ? `${currentAmount}/${currentCapacity}`
              : currentAmount}
          </td>
        </tr>
      );
    } else {
      return (
        <tr className="tableRow" key={cost.brickId}>
          <td className="tableCell">{itemName}</td>
          <td className="tableCell">{trueCost}</td>
          <td className="tableCell">
            <X size={32} className="costIcon missingCostIcon" />
          </td>
          <td className="tableCell">
            {costType === "brick"
              ? `${currentAmount}/${currentCapacity}`
              : currentAmount}
          </td>
        </tr>
      );
    }
  };

  return (
    <FullScreenModal
      header={packTitle}
      isOpen={isOpen}
      onClose={() => onClose(false)}
      helpURL="https://www.google.com"
    >
      <div className="fullModalParent">
        <div className="packDetails">
          <div className="packStats">
            <table>
              <tbody>
                <tr>
                  <td className="packStatColumn">Total Purchases:</td>
                  <td>
                    {state.brickPackStats.get(packId)?.toLocaleString() || 0}
                  </td>
                </tr>
                <tr>
                  <td className="packStatColumn">
                    Pack Compendium Unlock Progress
                  </td>
                  <td>
                    {renderBrickPackProgress()} /{" "}
                    {state.actions.get(packId)?.outcome.length}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <FullModalContainer title={"Cost Breakdown"}>
          <table className="customTable">
            <thead className="tableHead">
              <th>Item</th>
              <th>Cost</th>
              <th>Meet Costs</th>
              <th>Amount / Capacity</th>
            </thead>
            <tbody>
              {state.actions
                .get(packId)
                ?.cost.map((cost, index) => renderCosts(index, cost))}
            </tbody>
          </table>
        </FullModalContainer>
        <FullModalContainer title={"Rewards"}>
          <table className="customTable">
            <thead className="tableHead">
              <th>Brick Reward</th>
              <th>Find Percentage per purchase</th>
              <th>Amount / Capacity</th>
              <th>Added to Compendium</th>
            </thead>
            <tbody>
              {state.actions.get(packId)?.outcome.map((brick) => {
                // Calculate total find percentage including upgrades
                const basePercentage = brick.baseChangeValue;
                const upgradeCount = state.brickPackUpgrades.get(packId) || 0;
                const upgradeBonus = upgradeCount * 0.05;
                const totalPercentage = Math.min(
                  1.0,
                  basePercentage + upgradeBonus,
                );

                return (
                  <tr key={brick.brickId}>
                    <td>{state.bricks.get(brick.brickId)!.name}</td>
                    <td>{(totalPercentage * 100).toFixed(0)}%</td>
                    <td>
                      {Number.isNaN(state.bricks.get(brick.brickId)!.amount)
                        ? 0
                        : state.bricks.get(brick.brickId)!.amount}{" "}
                      / {state.bricks.get(brick.brickId)!.capacity}
                    </td>
                    <td>
                      {Number.isNaN(state.bricks.get(brick.brickId)!.amount) ? (
                        <X size={32} className="costIcon missingCostIcon" />
                      ) : (
                        <Check size={32} className="costIcon metCostIcon" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FullModalContainer>
        <FullModalContainer title={"Purchase History"}>
          {state.brickPackHistory.get(packId)?.map((purchase) => (
            <table className="customTable" key={purchase.timestamp}>
              <thead className="tableHead">
                <th>
                  <b>Purchase timestamp: </b>
                  {new Date(purchase.timestamp).toLocaleString()}
                </th>
                <th>
                  <b>Total Packs Purchased: </b>
                  {purchase.totalPacks}
                </th>
              </thead>
              <thead className="tableHeadSecondary">
                <th>Brick Reward</th>
                <th>Amount Received</th>
              </thead>
              <tbody>
                {Array.from(purchase.brickContents.entries()).map(
                  ([brickId, amount]) => (
                    <tr className="tableRow">
                      <td>{BrickInfoTotal.get(brickId)!.name}</td>
                      <td>{amount}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          ))}
        </FullModalContainer>
      </div>
    </FullScreenModal>
  );
};

export default BrickPackDetails;
